import type { Session } from '@orgus/protocol';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, FocusEvent, ReactNode } from 'react';
import type { ProjectGroup } from './filters.ts';
import { ESTIMATED_HEIGHTS, fitSlots, flattenGroups, layoutRows, mountedRows, rowAt, useScrollWindow, visibleRange } from './virtual.ts';
import type { RowHeights, VKind } from './virtual.ts';

/** Rows mounted beyond each edge of the viewport, in px: enough for a fast flick between frames. */
const OVERSCAN_PX = 800;

export interface VirtualGroupsProps {
  groups: ProjectGroup[];
  /** Project headers: off when one project is shown. */
  headers: boolean;
  renderProject: (g: ProjectGroup) => ReactNode;
  /** Render one session row (a SessionRow): it must put `style` on its root `li`. */
  renderSession: (s: Session, style: CSSProperties) => ReactNode;
}

/**
 * The grouped session list, windowed: one positioned row per project header and session, of
 * which only those near the viewport are mounted. The current project's header stays pinned
 * at the top while its rows scroll under it, as in the full list.
 */
export function VirtualGroups({ groups, headers, renderProject, renderSession }: VirtualGroupsProps) {
  const ref = useRef<HTMLUListElement>(null);
  const rows = useMemo(() => flattenGroups(groups, headers), [groups, headers]);
  const [heights, setHeights] = useState<RowHeights>(ESTIMATED_HEIGHTS);
  const { offsets, total } = useMemo(() => layoutRows(rows, heights), [rows, heights]);
  const win = useScrollWindow(ref);
  const [start, end] = visibleRange(offsets, win.top - OVERSCAN_PX, win.top + win.height + OVERSCAN_PX);

  // Every row of a kind gets one slot height: the tallest mounted row of that kind. Rows are one
  // height by design (h-row), but a measured pixel too few made the slot flip between two values,
  // each flip moving the window, until React stopped the loop (error 185). The first measure
  // replaces the estimate; after that slots only grow.
  const measured = useRef(new Set<VKind>());
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const next = fitSlots(heights, tallestMounted(el), measured.current);
    if (next) setHeights(next);
  });

  // The project whose rows are at the top of the viewport, once its own header has scrolled past.
  const topIdx = rowAt(offsets, Math.max(0, win.top));
  const topRow = rows[topIdx];
  const pinnedIdx = topRow ? (topRow.kind === 'project' ? topIdx : topRow.project) : -1;
  const pinned = pinnedIdx >= 0 && win.top > offsets[pinnedIdx]! ? rows[pinnedIdx] : undefined;

  // The row holding keyboard focus stays mounted when it scrolls out of the window.
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const focusedIdx = useMemo(() => (focusedKey ? rows.findIndex((r) => r.key === focusedKey) : -1), [rows, focusedKey]);
  const onFocus = (e: FocusEvent<HTMLUListElement>) => {
    let li = e.target as HTMLElement | null;
    while (li && li.parentElement !== e.currentTarget) li = li.parentElement;
    if (!li) return;
    // Every row's li is positioned at its own offset, so its top names the row.
    const i = rowAt(offsets, parseFloat(li.style.top) || 0);
    setFocusedKey(rows[i]?.key ?? null);
  };
  const onBlur = (e: FocusEvent<HTMLUListElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocusedKey(null);
  };

  const items: ReactNode[] = [];
  for (const i of mountedRows(start, end, focusedIdx)) {
    const r = rows[i]!;
    const style: CSSProperties = { position: 'absolute', top: offsets[i], left: 0, right: 0 };
    if (r.kind === 'project') {
      items.push(
        <li key={r.key} data-vkind="project" style={style}>
          {renderProject(r.group)}
        </li>,
      );
    } else {
      items.push(renderSession(r.session, style));
    }
  }

  return (
    <div className="relative">
      {pinned?.kind === 'project' && (
        // Not aria-hidden: the header holds a button ("New session in ...") that stays usable while
        // pinned, and a focusable control must not sit inside an aria-hidden subtree. The original
        // header has scrolled out of view, so this copy is the one people see and use.
        <div className="sticky top-0 z-(--z-sticky) h-0">
          <div className="absolute inset-x-0 top-0">{renderProject(pinned.group)}</div>
        </div>
      )}
      <ul ref={ref} className="relative" style={{ height: total }} onFocus={onFocus} onBlur={onBlur}>
        {items}
      </ul>
    </div>
  );
}

/** The tallest mounted row of each kind, in px (0 when none of that kind is mounted). */
function tallestMounted(list: HTMLElement): RowHeights {
  const tallest: RowHeights = { project: 0, session: 0 };
  for (const kind of ['project', 'session'] as VKind[]) {
    // Session rows are SessionRow's own li (with the pressable row inside); headers are ours.
    const nodes =
      kind === 'session'
        ? [...list.querySelectorAll<HTMLElement>(':scope > li > [data-session-row]')].map((n) => n.parentElement!)
        : [...list.querySelectorAll<HTMLElement>(`:scope > [data-vkind="${kind}"]`)];
    for (const n of nodes) tallest[kind] = Math.max(tallest[kind], n.getBoundingClientRect().height);
  }
  return tallest;
}
